import { Grid } from '@mui/material'
import {
    type GetCandidateProfileForRecruiterQuery,
    type GetCandidateProfileForRecruiterQueryVariables,
    RosterStatus,
} from '__generated__/graphql'
import { type DataGridProProps } from '@mui/x-data-grid-pro'
import { type Dispatch, type FC, type SetStateAction } from 'react'
import { useLocation } from 'react-router-dom'
import {
    RosterSearchResultsTable,
    type RosterTableRowModel,
} from 'recruiter/components/RosterSearchResultsTable'
import { PeopleRosterSearchesToolbar } from 'recruiter/components/RosterTableToolbar'
import { onSearchesColumns, otherSearchesColumns } from './peopleRosterColumns'
import AddToRosterSqlPanel from './RosterPanelContent/AddToRosterSqlPanel'
import RosterSqlPanelContent from './RosterPanelContent/RosterSqlPanelContent'
import { RosterSqlSearchProvider } from 'recruiter/contexts'
import { toGraphQLRosterFilters } from 'shared/hooks/rosterSqlSearchUrlState'
import { type ApolloQueryRefetchFunction } from 'shared/types/graphql'
import { getPxAsRem } from 'shared/utils'
import { type CandidateRosterTableViewId } from '.'
import { CandidateExperienceCards } from './Career/CandidateExperienceCards'
import Notes from './Notes'
import RecruitingPreferences from './RecruitingPreferences'

interface CandidateOverviewProps {
    data?: GetCandidateProfileForRecruiterQuery
    refetch: ApolloQueryRefetchFunction<
        GetCandidateProfileForRecruiterQuery,
        GetCandidateProfileForRecruiterQueryVariables
    >
    tableView: CandidateRosterTableViewId
    setTableView: Dispatch<SetStateAction<CandidateRosterTableViewId>>
}

const CandidateOverview: FC<CandidateOverviewProps> = ({
    data,
    refetch,
    tableView: _tableView,
    setTableView: _setTableView,
}) => {
    const location = useLocation()
    const routeTableView: CandidateRosterTableViewId =
        location.pathname.endsWith('/all') ? 'all' : 'on'
    const candidateId = data?.candidate?.id

    const transformFilters = (
        filters: Parameters<typeof toGraphQLRosterFilters>[0]
    ) => ({
        ...toGraphQLRosterFilters(filters),
        type: 'Search',
    })

    const getDetailPanelContentOn: DataGridProProps<RosterTableRowModel>['getDetailPanelContent'] =
        (params) => <RosterSqlPanelContent data={params} />

    const getDetailPanelContentNotOn: DataGridProProps<RosterTableRowModel>['getDetailPanelContent'] =
        (params) => (
            <AddToRosterSqlPanel
                data={params}
                candidateId={candidateId ?? ''}
            />
        )

    const getDetailPanelHeight = () => 'auto' as const

    return (
        <Grid container spacing={getPxAsRem(24)}>
            <Grid item xs={12} md={8} sx={{ height: { md: '100vh' } }}>
                <Notes data={data} refetch={refetch} />
            </Grid>

            <Grid
                item
                xs={12}
                md={4}
                display="flex"
                flexDirection="column"
                gap={getPxAsRem(24)}
                sx={{ height: { md: '100vh' } }}
            >
                <CandidateExperienceCards
                    sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
                />
                <RecruitingPreferences sx={{ flexShrink: 0 }} />
            </Grid>

            <Grid
                item
                xs={12}
                sx={{
                    height: '90vh',
                    display: 'flex',
                    overflow: 'hidden',
                }}
            >
                {routeTableView === 'on' ? (
                    <RosterSqlSearchProvider
                        defaultStatuses={[
                            RosterStatus.Active,
                            RosterStatus.Opportunistic,
                            RosterStatus.OnHold,
                            RosterStatus.Closed,
                        ]}
                        candidateId={candidateId}
                        transformFilters={transformFilters}
                        isRowSelectable={(params) =>
                            (params.row as RosterTableRowModel).canEdit ?? false
                        }
                    >
                        <RosterSearchResultsTable
                            storageKey="people-rosters-on-columns"
                            columns={onSearchesColumns}
                            toolbar={<PeopleRosterSearchesToolbar />}
                            containerProps={{ elevation: 1 }}
                            checkboxSelection
                            getDetailPanelContent={getDetailPanelContentOn}
                            getDetailPanelHeight={getDetailPanelHeight}
                        />
                    </RosterSqlSearchProvider>
                ) : (
                    <RosterSqlSearchProvider
                        defaultStatuses={[RosterStatus.Active]}
                        enrichmentCandidateId={candidateId}
                        transformFilters={transformFilters}
                        isRowSelectable={(params) =>
                            (params.row as RosterTableRowModel).canEdit ?? false
                        }
                    >
                        <RosterSearchResultsTable
                            storageKey="people-rosters-not-on-columns"
                            columns={otherSearchesColumns(candidateId ?? '')}
                            toolbar={<PeopleRosterSearchesToolbar />}
                            containerProps={{ elevation: 1 }}
                            checkboxSelection
                            getDetailPanelContent={(params) =>
                                (params.row as RosterTableRowModel)
                                    .rosterCandidateId
                                    ? getDetailPanelContentOn(params)
                                    : getDetailPanelContentNotOn(params)
                            }
                            getDetailPanelHeight={getDetailPanelHeight}
                        />
                    </RosterSqlSearchProvider>
                )}
            </Grid>
        </Grid>
    )
}

export default CandidateOverview
